import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  OwnerTransfer,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import type { MonetaryAmountFragment_monetaryAmount } from '@sorare/core/src/lib/__generated__/monetaryAmount.graphql';
import { isType } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { OwnerMyClubLink } from 'components/TokenPreview/TokenOwner';
import { InlineCard } from 'components/card/InlineCard';
import {
  Cell,
  SearchTableRow,
} from 'components/searchPlayerTable/SearchTable/ui';
import { TokenTransferTypeIcon } from 'components/token/TokenTransferTypeIcon';
import { getTransactionType, transactionTypeMessages } from 'lib/transactions';

import type { CompletedTransactionRow_deal } from './__generated__/index.graphql';

const StyledSearchTableRow = styled(SearchTableRow)`
  &:nth-last-child(2) {
    border-bottom: none;
  }
`;

const SidePadding = styled(Vertical)`
  padding: var(--unit) 0;
`;

const StartCell = styled(Cell)`
  justify-content: flex-start;
`;

type Props = {
  deal: CompletedTransactionRow_deal;
  user: { slug: string };
};

const SORARE_USER = 'SORARE_USER' as const;

const getTransferType = (trade: CompletedTransactionRow_deal) => {
  if (isType(trade, 'TokenOffer')) {
    return OwnerTransfer.TRANSFER;
  }
  if (isType(trade, 'TokenAuction')) {
    return OwnerTransfer.ENGLISH_AUCTION;
  }
  return OwnerTransfer.INSTANT_BUY;
};

const getTo = (trade: CompletedTransactionRow_deal, user: { slug: string }) => {
  if (isType(trade, 'TokenOffer')) {
    if (isType(trade.sender, 'User') && trade.sender.slug === user.slug) {
      return trade.receiver;
    }
    return trade.sender;
  }
  return SORARE_USER;
};

const getSenderSide = (
  trade: CompletedTransactionRow_deal,
  user: { slug: string }
) => {
  if (isType(trade, 'TokenOffer')) {
    if (isType(trade.sender, 'User') && trade.sender.slug === user.slug) {
      return trade.senderSide;
    }
    return trade.receiverSide;
  }
  if (isType(trade, 'TokenAuction')) {
    return { amounts: trade.bestBid?.amounts, anyCards: [] };
  }
  return { amounts: trade.price, anyCards: [] };
};

const getReceiverSide = (
  trade: CompletedTransactionRow_deal,
  user: { slug: string }
) => {
  if (isType(trade, 'TokenOffer')) {
    if (isType(trade.sender, 'User') && trade.sender.slug === user.slug) {
      return trade.receiverSide;
    }
    return trade.senderSide;
  }
  return { anyCards: trade.anyCards, amounts: undefined };
};

const To = ({
  seller,
  sport,
}: {
  seller:
    | typeof SORARE_USER
    | { __typename: 'AnonymousUser' }
    | ComponentProps<typeof OwnerMyClubLink>['user']
    | null;
  sport: Sport;
}) => {
  if (seller === SORARE_USER) {
    return <SorareUser variant="medium" />;
  }
  if (isType(seller, 'User')) {
    return (
      <OwnerMyClubLink
        user={seller}
        sport={sport}
        withAvatar
        variant="medium"
      />
    );
  }
  return <LabelM>-</LabelM>;
};

const Side = ({
  side,
}: {
  side: {
    anyCards: ComponentProps<typeof InlineCard>['anyCard'][];
    amounts: Nullable<MonetaryAmountFragment_monetaryAmount>;
  };
}) => {
  return (
    <StartCell>
      <SidePadding>
        {side.anyCards.map(card => (
          <InlineCard key={card.slug} anyCard={card} />
        ))}
        {!!side.amounts && !!side.amounts.wei && side.amounts.wei > 0 && (
          <>
            {side.anyCards.length > 0 && '+'}
            <AmountWithConversion
              hideExponent
              column
              monetaryAmount={side.amounts}
            />
          </>
        )}
      </SidePadding>
    </StartCell>
  );
};

export const CompletedTransactionRow = ({ deal, user }: Props) => {
  const { formatDistanceToNow, formatDate } = useIntlContext();
  const transactionType = getTransactionType({ deal });
  const transferType = getTransferType(deal);
  const receiverSide = getReceiverSide(deal, user);
  const senderSide = getSenderSide(deal, user);

  return (
    <StyledSearchTableRow key={deal.id}>
      <StartCell>
        <LabelM bold className="text-center">
          {!!deal.transactionDate && (
            <Tooltip title={formatDate(deal.transactionDate)}>
              {formatDistanceToNow(deal.transactionDate)}
            </Tooltip>
          )}
        </LabelM>
      </StartCell>
      <StartCell>
        <TokenTransferTypeIcon transferType={transferType} />
        <LabelM bold className="text-center">
          <FormattedMessage {...transactionTypeMessages[transactionType]} />
        </LabelM>
      </StartCell>
      <Side side={receiverSide} />
      <StartCell>
        <To seller={getTo(deal, user)} sport={Sport.FOOTBALL} />
      </StartCell>
      <Side side={senderSide} />
    </StyledSearchTableRow>
  );
};

CompletedTransactionRow.fragments = {
  deal: gql`
    fragment CompletedTransactionRow_deal on TokenOperationTradeInterface {
      id
      ...getTransactionType_tokenDeal
      transactionDate
      ... on TokenOffer {
        id
        senderSide {
          id
          amounts {
            ...MonetaryAmountFragment_monetaryAmount
          }
          anyCards {
            slug
            ...InlineCard_anyCardInterface
          }
        }
        receiverSide {
          id
          amounts {
            ...MonetaryAmountFragment_monetaryAmount
          }
          anyCards {
            slug
            ...InlineCard_anyCardInterface
          }
        }
        sender {
          ... on User {
            slug
            ...OwnerMyClubLink_User
          }
        }
        receiver {
          ... on User {
            slug
            ...OwnerMyClubLink_User
          }
        }
      }
      ... on TokenAuction {
        id
        bestBid {
          id
          amounts {
            ...MonetaryAmountFragment_monetaryAmount
          }
        }
        anyCards {
          slug
          ...InlineCard_anyCardInterface
        }
      }
      ... on TokenPrimaryOffer {
        id
        price {
          ...MonetaryAmountFragment_monetaryAmount
        }
        anyCards {
          slug
          ...InlineCard_anyCardInterface
        }
      }
    }
    ${monetaryAmountFragment}
    ${OwnerMyClubLink.fragments.user}
    ${getTransactionType.fragments.deal}
    ${InlineCard.fragments.anyCard}
  ` as TypedDocumentNode<CompletedTransactionRow_deal>,
};
